/**
 * BulkActionToolbar
 *
 * Appears above the table when one or more rows are selected.
 * Surfaces delete, update, and export actions.
 */

import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './BulkActionToolbar.css';

export interface BulkActionToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkDelete: () => void;
  onBulkUpdate: () => void;
  onBulkExport: (format: 'csv' | 'json') => void;
  canDelete?: boolean;
  canUpdate?: boolean;
}

const ForgeButton = 'forge-button' as React.ElementType;

export function BulkActionToolbar({
  selectedCount,
  onClearSelection,
  onBulkDelete,
  onBulkUpdate,
  onBulkExport,
  canDelete = true,
  canUpdate = true,
}: BulkActionToolbarProps): JSX.Element {
  return (
    <div className="bulk-action-toolbar" role="toolbar" aria-label="Bulk actions">
      <div className="bulk-action-toolbar__selection">
        <span className="forge-typography--body2 bulk-action-toolbar__count">
          {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
        </span>
        <ForgeButton
          variant="text"
          className="bulk-action-toolbar__clear"
          onClick={onClearSelection}
          type="button"
        >
          Clear selection
        </ForgeButton>
      </div>

      <div className="bulk-action-toolbar__actions">
        {canUpdate && (
          <ForgeButton variant="outlined" onClick={onBulkUpdate} type="button">
            <forge-icon name="edit" aria-hidden="true"></forge-icon>
            Update
          </ForgeButton>
        )}

        <div className="bulk-action-toolbar__export-group">
          <ForgeButton type="button" variant="outlined" onClick={() => onBulkExport('csv')}>
            <forge-icon name="download" aria-hidden="true"></forge-icon>
            Export CSV
          </ForgeButton>
          <ForgeButton type="button" variant="outlined" onClick={() => onBulkExport('json')}>
            Export JSON
          </ForgeButton>
        </div>

        {canDelete && (
          <ForgeButton
            variant="raised"
            className="bulk-action-toolbar__delete"
            onClick={onBulkDelete}
            type="button"
          >
            <forge-icon name="delete" aria-hidden="true"></forge-icon>
            Delete ({selectedCount})
          </ForgeButton>
        )}
      </div>
    </div>
  );
}
